import { useCallback, useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";

import { EnterpriseContext } from "@/context/EnterpriseContext";
import { UserContext } from "@/context/UserContext";
import {
  createPinnedItem,
  deletePinnedItem,
  extractRoleInfo,
  fetchPinnedItems,
  updatePinnedItem,
} from "@/lib/api";
import {
  CreatePinnedItemDto,
  PinnedItem,
  PinnedItemScope,
} from "@/types/pinnedItems";
import { trackEvent } from "@/utils/analytics/mixpanel";
import {
  canCreateEnterprisePin,
  canDeletePinnedItem,
  canEditPinnedItemScope,
} from "@/utils/pinnedItemPermissions";

const QUERY_KEY = "pinnedItems";

interface UsePinnedItemsOptions {
  enabled?: boolean;
}

interface UpdateItemPayload {
  scope?: PinnedItemScope;
}

interface UsePinnedItemsReturn {
  pinnedItems: PinnedItem[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  createItem: (dto: CreatePinnedItemDto) => Promise<PinnedItem>;
  updateItem: (itemId: string, updates: UpdateItemPayload) => Promise<void>;
  deleteItem: (itemId: string) => Promise<void>;
  reorderItems: (reorderedItems: PinnedItem[]) => Promise<void>;
  isPinningEnabled: boolean;
  canCreateEnterpriseScope: boolean;
  canDeleteItem: (item: PinnedItem) => boolean;
  canEditScope: boolean;
}

/**
 * Hook for interacting with the pinned items API.
 * Handles fetching, creating, deleting, and reordering pinned items.
 */
export const usePinnedItems = (
  options: UsePinnedItemsOptions = {}
): UsePinnedItemsReturn => {
  const router = useRouter();
  const { enterpriseId } = router.query as { enterpriseId?: string };
  const queryClient = useQueryClient();
  const enterpriseContext = useContext(EnterpriseContext);
  const { user } = useContext(UserContext);
  const isPinningEnabled =
    enterpriseContext?.featureToggles?.newHomepage ?? false;

  // Extract user role and ID for permission checks
  const { roleType } = extractRoleInfo(user);
  const userId = user?.id;
  const canCreateEnterpriseScope = canCreateEnterprisePin(roleType);
  const canEditScope = canEditPinnedItemScope(roleType);

  const queryKey = [QUERY_KEY, enterpriseId];

  // Determine if queries should be enabled
  const isEnabled =
    !!enterpriseId && isPinningEnabled && (options.enabled ?? true);

  // Fetch pinned items for the enterprise
  const {
    data: pinnedItems = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<PinnedItem[], Error>(
    queryKey,
    () => fetchPinnedItems(enterpriseId!),
    {
      enabled: isEnabled,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  // Mutation for creating pinned items
  const createMutation = useMutation(
    (dto: CreatePinnedItemDto) => createPinnedItem(enterpriseId!, dto),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  // Mutation for deleting pinned items
  const deleteMutation = useMutation(
    (itemId: string) => deletePinnedItem(enterpriseId!, itemId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  // Mutation for reordering pinned items with optimistic updates
  const reorderMutation = useMutation(
    async (reorderedItems: PinnedItem[]) => {
      // Update each item's displayOrder in parallel
      await Promise.all(
        reorderedItems.map((item, index) =>
          updatePinnedItem(enterpriseId!, item.id, { displayOrder: index })
        )
      );
    },
    {
      onMutate: async (reorderedItems: PinnedItem[]) => {
        // Cancel outgoing refetches
        await queryClient.cancelQueries(queryKey);

        // Snapshot the previous value
        const previousItems = queryClient.getQueryData<PinnedItem[]>(queryKey);

        // Optimistically update with new order
        const optimisticItems = reorderedItems.map((item, index) => ({
          ...item,
          displayOrder: index,
        }));
        queryClient.setQueryData(queryKey, optimisticItems);

        return { previousItems };
      },
      onError: (_error, _reorderedItems, context) => {
        // Roll back on error
        if (context?.previousItems) {
          queryClient.setQueryData(queryKey, context.previousItems);
        }
      },
      onSettled: () => {
        // Refetch to ensure sync with server
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  // Mutation for updating pinned items with optimistic updates
  const updateMutation = useMutation(
    ({ itemId, updates }: { itemId: string; updates: UpdateItemPayload }) =>
      updatePinnedItem(enterpriseId!, itemId, updates),
    {
      onMutate: async ({
        itemId,
        updates,
      }: {
        itemId: string;
        updates: UpdateItemPayload;
      }) => {
        // Cancel outgoing refetches
        await queryClient.cancelQueries(queryKey);

        // Snapshot the previous value
        const previousItems = queryClient.getQueryData<PinnedItem[]>(queryKey);

        // Optimistically update the item
        queryClient.setQueryData(queryKey, (old: PinnedItem[] | undefined) =>
          old?.map((item) =>
            item.id === itemId ? { ...item, ...updates } : item
          )
        );

        return { previousItems };
      },
      onError: (_error, _vars, context) => {
        // Roll back on error
        if (context?.previousItems) {
          queryClient.setQueryData(queryKey, context.previousItems);
        }
      },
      onSettled: () => {
        // Refetch to ensure sync with server
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  // Wrapper functions with feature flag and enterpriseId guards
  const createItem = useCallback(
    async (dto: CreatePinnedItemDto): Promise<PinnedItem> => {
      if (!isPinningEnabled) {
        throw new Error("Pinning feature is not enabled");
      }
      if (!enterpriseId) {
        throw new Error("enterpriseId is required");
      }
      const result = await createMutation.mutateAsync(dto);
      trackEvent({
        name: "Pinboard - Item pinned",
        properties: {
          enterpriseId,
          userId,
          itemType: dto.itemType,
          componentId: dto.componentId,
          scope: dto.scope || "PERSONAL",
        },
      });
      return result;
    },
    [isPinningEnabled, enterpriseId, createMutation, userId]
  );

  const updateItem = useCallback(
    async (itemId: string, updates: UpdateItemPayload): Promise<void> => {
      if (!isPinningEnabled) {
        throw new Error("Pinning feature is not enabled");
      }
      if (!enterpriseId) {
        throw new Error("enterpriseId is required");
      }
      await updateMutation.mutateAsync({ itemId, updates });
      // Track scope changes
      if (updates.scope) {
        trackEvent({
          name: "Pinboard - Item scope changed",
          properties: {
            enterpriseId,
            userId,
            itemId,
            newScope: updates.scope,
          },
        });
      }
    },
    [isPinningEnabled, enterpriseId, updateMutation, userId]
  );

  const deleteItem = useCallback(
    async (itemId: string): Promise<void> => {
      if (!isPinningEnabled) {
        throw new Error("Pinning feature is not enabled");
      }
      if (!enterpriseId) {
        throw new Error("enterpriseId is required");
      }
      // Find item to get type info for tracking before deletion
      const itemToDelete = pinnedItems.find((item) => item.id === itemId);
      await deleteMutation.mutateAsync(itemId);
      trackEvent({
        name: "Pinboard - Item unpinned",
        properties: {
          enterpriseId,
          userId,
          itemId,
          itemType: itemToDelete?.itemType,
          componentId: itemToDelete?.componentId,
        },
      });
    },
    [isPinningEnabled, enterpriseId, deleteMutation, pinnedItems, userId]
  );

  const reorderItems = useCallback(
    async (reorderedItems: PinnedItem[]): Promise<void> => {
      if (!isPinningEnabled) {
        throw new Error("Pinning feature is not enabled");
      }
      if (!enterpriseId) {
        throw new Error("enterpriseId is required");
      }
      await reorderMutation.mutateAsync(reorderedItems);
      trackEvent({
        name: "Pinboard - Items reordered",
        properties: {
          enterpriseId,
          userId,
          itemCount: reorderedItems.length,
        },
      });
    },
    [isPinningEnabled, enterpriseId, reorderMutation, userId]
  );

  // Permission check for deleting items
  const canDeleteItem = useCallback(
    (item: PinnedItem): boolean => {
      return canDeletePinnedItem(item, userId, roleType);
    },
    [userId, roleType]
  );

  return {
    pinnedItems,
    isLoading,
    isError,
    error: error || null,
    refetch,
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
    isPinningEnabled,
    canCreateEnterpriseScope,
    canDeleteItem,
    canEditScope,
  };
};
